import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { KairosShowcase } from './KairosShowcase';
import { KineticObject } from './KineticObject';
import { ResearchStudy } from './ResearchStudy';
import { ImmersiveBackground } from './ImmersiveBackground';
import { useMotionPreference } from '../hooks/useMotionPreference';
import { useScrollChoreography } from '../hooks/useScrollChoreography';
import '../styles/landing.css';

const Arrow: React.FC<{ diagonal?: boolean; className?: string }> = ({ diagonal = false, className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d={diagonal ? 'M5 19 19 5M5 5h14v14' : 'M4 12h16m-7-7 7 7-7 7'} stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const research = [
  { name: 'Scribe', status: 'Prototype', summary: 'From conversation to a clear plan.', description: 'Turn unstructured discussions into actionable requirements and architectural decisions. Built around the way engineering teams think and work.', focus: 'Requirements · Engineering workflows' },
  { name: 'Prism', status: 'Internal tool', summary: 'A clearer view of AI performance.', description: 'Compare system latency, token efficiency, and interface responsiveness across experimental AI models. Understand the tradeoffs behind each implementation.', focus: 'Model evaluation · System performance' },
  { name: 'Morpheus', status: 'Research', summary: 'Better reasoning when evidence is incomplete.', description: 'Explore competing hypotheses, forecast possible outcomes, and revise conclusions as new evidence emerges. Research into decisions that demand a deeper level of reasoning.', focus: 'Reasoning · Decision support' },
];

export const LandingPage: React.FC = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { motionPaused, reducedMotion, motionDisabled, toggleMotion } = useMotionPreference();
  const [activeResearch, setActiveResearch] = useState(0);
  const [openResearch, setOpenResearch] = useState<number | null>(0);
  const pageRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLElement>(null);
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const mainRef = useRef<HTMLElement>(null);
  const footerRef = useRef<HTMLElement>(null);
  useScrollChoreography(pageRef, motionDisabled);

  useEffect(() => {
    const elements = pageRef.current?.querySelectorAll<HTMLElement>('[data-reveal]');
    if (!elements || !('IntersectionObserver' in window)) return;
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-revealed');
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.1 });
    elements.forEach(element => {
      element.classList.add('will-reveal');
      observer.observe(element);
    });
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    const scrollToHash = () => {
      let id = window.location.hash.slice(1);
      try { id = decodeURIComponent(id); } catch { /* Invalid fragments have no matching section. */ }
      if (id) document.getElementById(id)?.scrollIntoView();
    };
    onScroll();
    const frame = requestAnimationFrame(scrollToHash);
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('hashchange', scrollToHash);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('hashchange', scrollToHash);
    };
  }, []);

  useEffect(() => {
    if (!menuOpen) return;
    const previousOverflow = document.body.style.overflow;
    const main = mainRef.current;
    const footer = footerRef.current;
    document.body.style.overflow = 'hidden';
    main?.setAttribute('inert', '');
    footer?.setAttribute('inert', '');
    headerRef.current?.querySelector<HTMLAnchorElement>('#cx-mobile-nav a')?.focus();
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        setMenuOpen(false);
        menuButtonRef.current?.focus();
      }
      if (event.key === 'Tab') {
        const controls = headerRef.current?.querySelectorAll<HTMLElement>('a[href], button:not([disabled])');
        const visible = controls ? Array.from(controls).filter(el => el.getClientRects().length > 0) : [];
        const first = visible[0];
        const last = visible[visible.length - 1];
        const outsideHeader = !headerRef.current?.contains(document.activeElement);
        if (event.shiftKey && (document.activeElement === first || outsideHeader)) { event.preventDefault(); last?.focus(); }
        else if (!event.shiftKey && (document.activeElement === last || outsideHeader)) { event.preventDefault(); first?.focus(); }
      }
    };
    const onResize = () => { if (window.innerWidth > 800) setMenuOpen(false); };
    window.addEventListener('keydown', onKey);
    window.addEventListener('resize', onResize);
    return () => {
      document.body.style.overflow = previousOverflow;
      main?.removeAttribute('inert');
      footer?.removeAttribute('inert');
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', onResize);
    };
  }, [menuOpen]);

  const closeMenu = (id?: string) => {
    setMenuOpen(false);
    if (id) requestAnimationFrame(() => document.getElementById(id)?.focus({ preventScroll: true }));
    else menuButtonRef.current?.focus();
  };
  const openKairos = (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.currentTarget.href = `https://kairos.cognixisai.com?returnUrl=${encodeURIComponent(window.location.origin + window.location.pathname)}`;
  };

  return (
    <div className={`cx-site${motionDisabled ? ' cx-motion-paused' : ''}`} ref={pageRef}>
      <ImmersiveBackground disabled={motionDisabled} />
      <a className="cx-skip" href="#main" tabIndex={menuOpen ? -1 : 0}>Skip to content</a>
      <header ref={headerRef} className={`cx-header${scrolled ? ' is-scrolled' : ''}${menuOpen ? ' is-open' : ''}`}>
        <span className="cx-reading-progress" aria-hidden="true" />
        <a href="#top" className="cx-logo" aria-label="Cognixis home" onClick={() => closeMenu('top')}><img src="/cognixis-logo-site.png" alt="Cognixis" width="180" height="56" /></a>
        <nav className="cx-nav" aria-label="Primary navigation">
          <a href="#work">Our work</a><a href="#company">Company</a><a href="#lab">The lab</a>
        </nav>
        <a className="cx-header-cta" href="https://kairos.cognixisai.com" target="_blank" rel="noopener noreferrer" onClick={openKairos}>Explore Kairos <Arrow diagonal /></a>
        <button type="button" className="cx-header-motion" aria-label={reducedMotion ? 'Reduced motion enabled by your system' : motionPaused ? 'Resume motion' : 'Pause motion'} aria-pressed={motionDisabled} disabled={reducedMotion} onClick={toggleMotion} title={reducedMotion ? 'Reduced motion enabled by your system' : motionPaused ? 'Resume motion' : 'Pause motion'}><span /><span /><span /></button>
        <button ref={menuButtonRef} className="cx-menu-toggle" type="button" onClick={() => setMenuOpen(!menuOpen)} aria-expanded={menuOpen} aria-controls="cx-mobile-nav" aria-label={menuOpen ? 'Close navigation' : 'Open navigation'}><span /><span /></button>
        {menuOpen && <nav id="cx-mobile-nav" className="cx-mobile-nav" aria-label="Mobile navigation">
          <span className="cx-label">Explore Cognixis</span>
          <a href="#work" onClick={() => closeMenu('work')}>Our work <Arrow /></a>
          <a href="#company" onClick={() => closeMenu('company')}>Company <Arrow /></a>
          <a href="#lab" onClick={() => closeMenu('lab')}>The lab <Arrow /></a>
          <a href="https://kairos.cognixisai.com" target="_blank" rel="noopener noreferrer" onClick={event => { openKairos(event); closeMenu(); }}>Explore Kairos <Arrow diagonal /></a>
          <span className="cx-mobile-note">Intelligence, made useful.</span>
        </nav>}
      </header>

      <main ref={mainRef} id="main" tabIndex={-1}>
        <div className="cx-hero-shell">
        <section id="top" className="cx-hero" tabIndex={-1} aria-labelledby="hero-heading">
          <KineticObject variant="home" className="cx-hero-object" paused={motionDisabled} label="the intelligence sculpture" />
          <div className="cx-hero-study-note" aria-hidden="true"><span className="cx-study-cross">+</span><span>Intelligence in motion.<small>An ongoing exploration</small></span></div>
          <div className="cx-hero-content">
            <p className="cx-label cx-enter cx-enter-1"><span className="cx-dash" /> Cognixis · Applied intelligence</p>
            <h1 id="hero-heading"><span className="cx-heading-line"><span>Intelligence.</span></span><span className="cx-heading-line"><em>Made useful.</em></span></h1>
            <p className="cx-hero-description cx-enter cx-enter-3">We turn AI research into thoughtful software.<br className="cx-desktop-break" /> Built for real problems. Designed around people.</p>
            <div className="cx-hero-actions cx-enter cx-enter-4">
              <a className="cx-button cx-button-lime" href="#work">Discover our work <Arrow diagonal /></a>
              <Link className="cx-text-link" to="/about">Meet Cognixis <Arrow /></Link>
            </div>
          </div>
          <div className="cx-hero-bottom cx-enter cx-enter-4"><span>Research. Engineering. Real-world impact.</span><a href="#work">Scroll to explore <span>↓</span></a></div>
        </section>
        </div>

        <section id="work" className="cx-feature cx-section" tabIndex={-1} aria-labelledby="kairos-heading">
          <div className="cx-section-top" data-reveal><p className="cx-label"><span>01 /</span> Selected work</p><span className="cx-live"><i /> Available now</span></div>
          <div id="kairos" className="cx-feature-grid">
            <div className="cx-feature-copy" data-reveal>
              <KineticObject variant="index-kairos" className="cx-feature-object cx-kinetic--paper" paused={motionDisabled} label="the Kairos orbital sculpture" />
              <p className="cx-product-name">Kairos</p>
              <h2 id="kairos-heading">Your experience.<br /><em>Your next move.</em></h2>
              <p>A clearer path from the role you want to the interview that matters. Understand the opportunity, shape your application, and prepare with purpose.</p>
              <a className="cx-button cx-button-ink" href="https://kairos.cognixisai.com" target="_blank" rel="noopener noreferrer" onClick={openKairos}>Explore Kairos <Arrow diagonal /></a>
              <span className="cx-product-note">AI-assisted career preparation. Review outputs; results are not guaranteed. <Link to="/ai-disclosure">AI use &amp; limitations</Link>.</span>
            </div>
            <div className="cx-product-visual" data-reveal><KairosShowcase paused={motionDisabled} /></div>
          </div>
        </section>

        <section id="company" className="cx-company cx-section" tabIndex={-1} aria-labelledby="company-heading">
          <div className="cx-section-top" data-reveal><p className="cx-label"><span>02 /</span> The way we think</p><span className="cx-label cx-section-aside">Built with intent</span></div>
          <div className="cx-company-lead" data-reveal><h2 id="company-heading">Complex problems.<br /><em>Considered solutions.</em></h2><div><p>Good technology earns its place. We focus on the moments where intelligence can make work clearer, decisions easier, and new possibilities real.</p><Link className="cx-text-link" to="/about">Inside Cognixis <Arrow diagonal /></Link></div></div>
          <div className="cx-principles">
            <article data-reveal><span className="cx-principle-index">01</span><div className="cx-principle-symbol cx-symbol-focus" aria-hidden="true"><i /><i /></div><h3>Start with the problem.</h3><p>Find the friction. Understand the workflow. Build where AI can make a meaningful difference.</p></article>
            <article data-reveal><span className="cx-principle-index">02</span><div className="cx-principle-symbol cx-symbol-build" aria-hidden="true"><i /><i /><i /></div><h3>Engineer for real life.</h3><p>Connect thoughtful interfaces with dependable systems. Make complexity feel simple.</p></article>
            <article data-reveal><span className="cx-principle-index">03</span><div className="cx-principle-symbol cx-symbol-refine" aria-hidden="true"><i /><i /><i /></div><h3>Keep making it better.</h3><p>Learn from actual use. Test the assumptions. Refine the details that move the work forward.</p></article>
          </div>
        </section>

        <section id="lab" className="cx-lab cx-section" tabIndex={-1} aria-labelledby="lab-heading">
          <div className="cx-section-top" data-reveal><p className="cx-label"><span>03 /</span> In the lab</p><span className="cx-label cx-section-aside">An ongoing exploration</span></div>
          <div className="cx-lab-lead" data-reveal><div><h2 id="lab-heading">What comes <em>next.</em></h2><p>Ideas taking shape. From early research<br className="cx-desktop-break" /> to tools we use every day.</p></div><span className="cx-lab-invitation">Select an idea.<br /><em>Explore the thinking.</em></span></div>
          <div className="cx-lab-grid">
          <div className="cx-research-list" data-reveal>
            {research.map((project, index) => <details className="cx-research" key={project.name} open={openResearch === index}>
              <summary onClick={event => { event.preventDefault(); setOpenResearch(value => value === index ? null : index); setActiveResearch(index); }}><span className="cx-research-number">0{index + 1}</span><h3>{project.name}</h3><span className="cx-research-summary">{project.summary}</span><span className="cx-research-status"><i />{project.status}</span><span className="cx-expand" aria-hidden="true" /></summary>
              <div className="cx-research-detail"><p>{project.description}</p><span className="cx-label">{project.focus}</span><Link className="cx-text-link" to={`/blog#journal-${project.name.toLowerCase()}`}>Read about {project.name} <Arrow diagonal /></Link></div>
            </details>)}
          </div>
          <ResearchStudy selected={activeResearch} />
          </div>
          <div className="cx-lab-foot" data-reveal><span>Research and prototypes may change; release is not guaranteed.</span><Link className="cx-text-link" to="/about">Our approach to AI <Arrow diagonal /></Link></div>
        </section>

        <section id="contact" className="cx-contact cx-section" aria-labelledby="contact-heading">
          <KineticObject variant="index-contact" className="cx-contact-object" paused={motionDisabled} label="the aperture sculpture" />
          <p className="cx-label" data-reveal><span className="cx-dash" /> Make the next thing matter</p>
          <a className="cx-contact-link" href="mailto:support@cognixisai.com?subject=Project%20inquiry" data-reveal><h2 id="contact-heading">What could we<br /><em>build together?</em></h2><span className="cx-contact-arrow"><Arrow diagonal /></span></a>
          <div className="cx-contact-bottom" data-reveal><p>Tell us what you’re working on.</p><a className="cx-text-link" href="mailto:support@cognixisai.com">support@cognixisai.com <Arrow diagonal /></a></div>
        </section>
      </main>

      <footer ref={footerRef} className="cx-footer">
        <div className="cx-footer-top"><a href="#top" className="cx-logo" aria-label="Cognixis home"><img src="/cognixis-logo-site.png" alt="Cognixis" width="180" height="56" /></a><nav aria-label="Footer navigation"><Link to="/about">Company</Link><Link to="/careers">Careers</Link><Link to="/blog">Ideas</Link><a href="https://kairos.cognixisai.com" target="_blank" rel="noopener noreferrer" onClick={openKairos}>Kairos <Arrow diagonal /></a></nav><a className="cx-back-top" href="#top">Back to top ↑</a></div>
        <div className="cx-footer-bottom"><span>© {new Date().getFullYear()} Cognixis LLC</span><button type="button" className="cx-motion-toggle" aria-pressed={motionDisabled} disabled={reducedMotion} onClick={toggleMotion}><span aria-hidden="true">{motionDisabled ? '▷' : 'Ⅱ'}</span>{reducedMotion ? 'Reduced motion' : motionPaused ? 'Resume motion' : 'Pause motion'}</button><nav aria-label="Legal"><Link to="/privacy-policy">Privacy</Link><Link to="/terms-of-service">Terms</Link><Link to="/cookie-policy">Cookies</Link><Link to="/ai-disclosure">AI disclosure</Link></nav></div>
      </footer>
    </div>
  );
};
